import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import { CoinRatioPlaceholder } from "@/components/brand/settings/progression/coin-ratio-placeholder"
import { ProgressionSummaryStrip } from "@/components/brand/settings/progression/progression-summary-strip"
import { RankLadderForm } from "@/components/brand/settings/progression/rank-ladder-form"
import { XpRulesForm } from "@/components/brand/settings/progression/xp-rules-form"
import HeadingSmall from "@/components/heading-small"
import AppLayout from "@/layouts/app-layout"
import type { BrandSettingsProgressionProps } from "@/types/progression"

/**
 * OKS-52 — Brand Settings → Progression page.
 *
 * Three vertically-stacked cards (UX Spec §2.2):
 *   - Rank ladder (in-place editor for the brand's N tiers + their
 *     privileges)
 *   - XP rules (4 number inputs: base / on-time / engagement /
 *     engagement view threshold)
 *   - Coin-to-XP V2 placeholder (static Alert; deferred until the
 *     Coins ledger lands — PRD OQ-1 / TRD KD-1)
 *
 * Each card hosts an independent Inertia `<Form method="patch">`
 * targeting its own endpoint, so an error in one save never blocks
 * the other (PRD §3 / TRD KD-3). Read-only roles see `canEdit:false`
 * → inputs `readOnly` + Save buttons replaced by a muted "Members
 * can view but not edit" caption.
 */
export default function BrandSettingsProgression({
  ladder,
  xp_rules,
  ranges,
  can_edit,
}: BrandSettingsProgressionProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("settings.brand.progression.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-progression" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.progression.title")}
            description={t("settings.brand.progression.description")}
          />

          <ProgressionSummaryStrip
            rankCount={ladder.length}
            baseXp={xp_rules.xp_base_per_mission}
          />

          <RankLadderForm ladder={ladder} canEdit={can_edit} />
          <XpRulesForm xpRules={xp_rules} ranges={ranges} canEdit={can_edit} />
          <CoinRatioPlaceholder />
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
